<template>
  <div>
    <sidebar v-if="showSidebar" class="p-0 w-100">
      <sidebar-signed v-if="signed" />
      <sidebar-with-signature-form v-else id="take-action" />
    </sidebar>

    <!-- These modals need to live here, outside the floating-sidebar,
         because the floating-sidebar has a z-index situation that interferes with proper modal operation. -->
    <confirm-your-email-modal v-if="awaitingEmailConfirmation" :email-address="action.email"
                              :email-sent-message="i18n.t('public.confirm_your_email_modal.sent_for_signature')"
                              :warning-message="i18n.t('public.petitions.confirm_email_warning')" />

    <prefill-forms-consent-modal v-if="requestingPrefillFormsConsent" />

    <toast v-if="toastErrorMessage" kind="error">
      <template #message-content>
        <p>{{ toastErrorMessage }}</p>
      </template>
    </toast>

    <template v-if="showFixedSignButton">
      <modal-with-signature-form v-if="showingActionFormModal" class="action-form-modal" @modal-closed="hideActionFormModal" />

      <div v-else class="bottom-fixed d-grid d-md-none p-3">
        <progress-bar v-if="showProgressBarAboveFixedSignButtom" :narrow-layout="true" />

        <button type="button" class="btn btn-primary btn-lg btn-show-signature-form" @click="showActionFormModal">
          {{ submitButtonText }}
        </button>
      </div>
    </template>

    <daisy-chain-manager v-if="showDaisyChain" />

    <generic-error-modal v-if="nonRecoverableError" :error-message="nonRecoverableError" />
  </div>
</template>

<script>
import ConfirmYourEmailModal from '@/components/confirm-your-email-modal'
import DaisyChainManager from '@/daisy-chains/manager'
import GenericErrorModal from '@/components/generic-error-modal.vue'
import { mapGetters, mapMutations, mapState } from 'vuex'
import ModalWithSignatureForm from './modal-with-signature-form'
import PrefillFormsConsentModal from '@/campaign-action-shared/prefill-forms-consent-modal'
import ProgressBar from './progress-bar'
import SidebarSigned from './sidebar-signed'
import SidebarWithSignatureForm from './sidebar-with-signature-form'
import Sidebar from '@/campaign-action-shared/sidebar'
import Toast from '@/components/toast'
import TopLevelMixin from './top-level-mixin'
import { trackEvent } from '@/analytics/track'

export default {
  components: {
    ConfirmYourEmailModal, DaisyChainManager, GenericErrorModal, ModalWithSignatureForm, PrefillFormsConsentModal,
    ProgressBar, SidebarWithSignatureForm, SidebarSigned, Sidebar, Toast
  },

  mixins: [ TopLevelMixin ],

  computed: {
    ...mapGetters(['showFixedSignButton', 'showProgressBarAboveFixedSignButtom', 'showSidebar', 'submitButtonText']),
    ...mapState(['action', 'alreadySignedOnPageLoad', 'awaitingEmailConfirmation', 'baseDaisyChainsPath',
                 'completingAction', 'daisyChainRepeatedAction', 'nonRecoverableError', 'petition', 'requestingPrefillFormsConsent',
                 'showDaisyChain', 'showingActionFormModal', 'signatureFormHidden', 'signed', 'toastErrorMessage'])
  },

  created() {
    window.addEventListener('resize', this.updateWindowWidth)
  },

  mounted() {
    if (this.completingAction) {
      if (!this.awaitingEmailConfirmation) {
        this.$store.dispatch('startDaisyChain', {
          baseDaisyChainsPath: this.baseDaisyChainsPath,
          repeatedAction: this.daisyChainRepeatedAction
        })
      }
    }
    else if (this.alreadySignedOnPageLoad) {
      // On page load, the petition is already signed by the user.
      trackEvent('Saw petition already signed', {
        action: 'already-signed',
        category: 'petition',
        name: this.petition.slug,
        petition: this.petition.slug
      })
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth)
  },

  methods: {
    ...mapMutations(['hideActionFormModal', 'showActionFormModal']),

    updateWindowWidth() {
      this.$store.commit('setWindowWidth', window.innerWidth)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~phoenix_styles/colors" as colors;

.bottom-fixed {
  background-color: #FFFFFF;
}
::v-deep(.action-form-modal .modal-content) {
  background: colors.$background-light-gray;
}
</style>
